import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  describeRejection,
  findLikelyDuplicate,
  reviewReadiness,
  type Receipt,
  type Transaction,
} from '@budgetmonkey/shared'
import { Badge, Button, Card, ErrorNote, Field, Input, Money } from '@/components/ui'
import { BudgetCodeField } from '@/features/budgetCodes/BudgetCodePicker'
import { useCodesById } from '@/features/budgetCodes/hooks'
import type { BudgetCode } from '@budgetmonkey/shared'
import { chargeableAmount, findSamePurchase } from '@budgetmonkey/shared'
import {
  TransactionForm,
  type TransactionFormValues,
} from '@/features/transactions/TransactionForm'
import {
  useCreateTransaction,
  useTransactions,
  useUpdateTransaction,
} from '@/features/transactions/hooks'
import { TransactionDetail } from '@/features/transactions/TransactionDetail'
import { usePeriods } from '@/features/reconciliation/hooks'
import { formatDate } from '@/lib/dates'
import { receiptFileUrl } from './api'
import {
  useChargesByReceipt,
  useLinkReceipt,
  useReprocessReceipt,
  useUpdateReceipt,
} from './hooks'

/**
 * Receipt review — REQUIREMENTS.md §7.1 steps 6 and 7.
 *
 * The screen where extraction either pays off or admits it didn't. Three
 * shapes, chosen by `reviewReadiness`:
 *
 *   confirm — a clean, high-confidence read. Everything is already known
 *             except the budget code, so that's the only thing asked for.
 *   correct — something was read but flagged. The form opens pre-filled and
 *             says what was refused and why.
 *   manual  — nothing usable came back. The form opens empty rather than
 *             pretending a guess is a reading.
 *
 * The distinction matters because the legacy system had only one mode: accept
 * whatever was read, silently. Matt found the wrong values at packet time,
 * weeks later. A refusal that explains itself costs him seconds here instead.
 */
export function ReceiptReview({
  receipt,
  onClose,
}: {
  receipt: Receipt
  onClose: () => void
}) {
  const create = useCreateTransaction()
  const link = useLinkReceipt()
  const updateCharge = useUpdateTransaction()
  const reprocess = useReprocessReceipt()
  const trustSender = useUpdateReceipt()
  const { data: transactions = [] } = useTransactions()
  const { data: periods = [] } = usePeriods()
  // What this receipt is already on. Read from the charges rather than from
  // the receipt's own link: a bill can sit behind several charges.
  const chargesByReceipt = useChargesByReceipt()
  const codesById = useCodesById()
  const filedAgainst = chargesByReceipt.get(receipt.id ?? '') ?? []

  const [error, setError] = useState<string>()
  const [code, setCode] = useState<BudgetCode | null>(null)
  // Typed on the confirm panel and carried into the full form if the user
  // opens it. It prints on the packet's "Brief Purchase Description" column,
  // so filing without it means coming back to this charge later.
  const [description, setDescription] = useState('')
  const [mode, setMode] = useState<'file' | 'attach'>('file')
  // "No — this is a separate purchase" has to be remembered, or the prompt
  // stays on screen with its primary button still offering the merge. It used
  // to call setMode('file'), which is the state the panel already starts in,
  // so pressing it did nothing at all.
  const [dismissedSamePurchase, setDismissedSamePurchase] = useState(false)
  // Escape hatch from the one-click path: a clean read can still be wrong in a
  // way only Matt can see, and the confirm panel has nothing to edit.
  const [forceForm, setForceForm] = useState(false)
  // A filed receipt opens showing what it is filed against and nothing else.
  // Leaving the filing form on screen put a required, empty budget code under
  // a receipt that already had one — which reads as unfinished work, and was
  // the first thing anyone asked about.
  const [addingAnother, setAddingAnother] = useState(false)
  // Editing the charge from here rather than sending Matt to Transactions to
  // do it. The receipt and the charge are two views of one purchase, and
  // noticing a wrong amount while looking at the paper is exactly when it gets
  // corrected.
  const [editingChargeId, setEditingChargeId] = useState<string>()

  const extraction = receipt.extraction
  const readiness = reviewReadiness({
    status: extraction?.status ?? null,
    needsReview: extraction?.needsReview ?? false,
    vendor: receipt.extractedVendor,
    amount: receipt.extractedAmount,
    date: receipt.extractedDate,
  })

  /** Every charge except the ones this receipt is already filed against. */
  const candidateCharges = useMemo(
    () => transactions.filter((t) => !(t.receiptIds ?? []).includes(receipt.id ?? '')),
    [transactions, receipt.id],
  )

  /**
   * Charges on a packet already handed to the business office.
   *
   * The same-purchase prompt *rewrites* a charge's amount, so it must not be
   * offered for one of these: regenerating that period would print different
   * totals from the copy in the business office's hands, which is exactly what
   * `mergeCharges` refuses and what `deleteTransaction` refuses. Withheld here
   * and refused in `attachAsSamePurchase` — the screen is the courtesy, the
   * refusal is the gate.
   */
  const submittedChargeIds = useMemo(
    () =>
      new Set(
        periods
          .filter((period) => period.status === 'submitted')
          .flatMap((period) => period.orderedTransactionIds),
      ),
    [periods],
  )

  /**
   * Charges the same-purchase prompt may offer.
   *
   * A void charge is out of every total and is not waiting for its other
   * slip; raising its amount would quietly change a correction someone made
   * on purpose.
   */
  const samePurchaseCandidates = useMemo(
    () =>
      candidateCharges.filter(
        (t) => t.status !== 'void' && !submittedChargeIds.has(t.id ?? ''),
      ),
    [candidateCharges, submittedChargeIds],
  )

  // §6.1's last rule. The legacy packet carried the same $150.36 tolls charge
  // twice; catching it here is the difference between a duplicate the reviewer
  // spots and one that reaches the district.
  const duplicate = useMemo(
    () =>
      findLikelyDuplicate(
        {
          vendor: receipt.extractedVendor,
          amount: receipt.extractedAmount,
          captureSource: receipt.captureSource,
          // The *purchase* date, not the upload time. These were compared
          // against the charge's purchase date, which only agrees when the
          // photo is taken the same day. Re-photographing a slip from a trip
          // three weeks ago put nineteen days between them, so the check said
          // nothing and a second $48.40 charge was filed for a dinner already
          // in the ledger — 2026-11-02.
          capturedAt: receipt.extractedDate ?? receipt.capturedAt,
        },
        // Charges this receipt is *already* on are not duplicates of it —
        // they are where it lives. Without this exclusion, opening a filed
        // receipt warned that its own charge "may already be filed", which
        // is true, unhelpful, and alarming enough to make someone go
        // looking for a mistake that is not there. Reported 2026-11-03.
        candidateCharges.map((t) => ({
          vendor: t.vendor,
          amount: t.amount,
          captureSource: receipt.captureSource,
          capturedAt: t.transactionDate,
          transaction: t,
        })),
      ),
    [receipt, candidateCharges],
  )

  /**
   * The other photo of the same purchase — a restaurant hands over an
   * itemised slip and then a signed one with the tip added. The amounts
   * differ by definition, so `findLikelyDuplicate` above never sees them;
   * filing both the obvious way produces two charges and takes the meal off
   * the budget twice.
   */
  const samePurchase = useMemo(
    () =>
      filedAgainst.length > 0 || dismissedSamePurchase
        ? null
        : findSamePurchase(
            {
              vendor: receipt.extractedVendor,
              amount: receipt.extractedAmount,
              at: receipt.extractedDate ?? receipt.capturedAt,
            },
            samePurchaseCandidates.map((t) => ({
              vendor: t.vendor,
              amount: t.amount,
              at: t.transactionDate,
              transaction: t,
            })),
          ),
    [receipt, samePurchaseCandidates, filedAgainst.length, dismissedSamePurchase],
  )

  const pending = create.isPending || link.isPending || updateCharge.isPending

  async function fileNew(values: TransactionFormValues) {
    setError(undefined)
    try {
      const { id: transactionId } = await create.mutateAsync(values)
      await link.mutateAsync({ receiptId: receipt.id!, transactionId })
      onClose()
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Could not file this receipt. Nothing was saved.',
      )
    }
  }

  /**
   * Put this photo on the charge it belongs to, and make sure the charge
   * carries the amount the statement will show.
   *
   * The tipped total is always the larger of the two, and it is the only one
   * the card was charged. If the itemised slip was filed first the charge is
   * understated, so attaching the signed slip corrects it — otherwise the
   * reconciliation has a charge that no statement line can match.
   */
  async function attachAsSamePurchase(transaction: Transaction) {
    setError(undefined)
    // The gate, not the courtesy. The screen does not offer these, and this
    // refuses them anyway: raising the amount of a charge the business office
    // is holding makes the stored packet disagree with the ledger, and a
    // regenerate would print a total unlike the copy handed in.
    if (submittedChargeIds.has(transaction.id ?? '')) {
      setError(
        'That charge is on a packet that has already been submitted, so its amount cannot be changed. File this photo on its own charge, or attach it from the Transactions screen without changing the total.',
      )
      return
    }
    if (transaction.status === 'void') {
      setError('That charge has been voided, so it is not waiting for another slip.')
      return
    }
    try {
      const counted = chargeableAmount(transaction.amount, receipt.extractedAmount ?? 0)
      if (counted !== transaction.amount) {
        await updateCharge.mutateAsync({
          id: transaction.id!,
          patch: { amount: counted },
        })
      }
      await link.mutateAsync({ receiptId: receipt.id!, transactionId: transaction.id! })
      onClose()
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Could not add this photo to that charge.',
      )
    }
  }

  async function attachExisting(transaction: Transaction) {
    setError(undefined)
    try {
      await link.mutateAsync({
        receiptId: receipt.id!,
        transactionId: transaction.id!,
      })
      onClose()
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Could not attach this receipt.',
      )
    }
  }

  const editingCharge = filedAgainst.find((t) => t.id === editingChargeId)
  if (editingCharge !== undefined) {
    return (
      <TransactionDetail
        transactionId={editingCharge.id!}
        codesById={codesById}
        fiscalYear={editingCharge.fiscalYear}
        onClose={() => setEditingChargeId(undefined)}
      />
    )
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
      <div className="space-y-4">
        <ReceiptImage receipt={receipt} />
        <ExtractionSummary
          receipt={receipt}
          readiness={readiness}
          onRetry={() => reprocess.mutate(receipt.id!)}
          retrying={reprocess.isPending}
          retryError={
            reprocess.error instanceof Error ? reprocess.error.message : undefined
          }
        />
      </div>

      <div className="space-y-5">
        {error !== undefined && <ErrorNote>{error}</ErrorNote>}

        {/* §6.1: a forward from an untrusted address is confirmed before it's
            acted on. It was still ingested — dropping it would lose a real
            receipt the first time a vendor changed its sending address. */}
        {!receipt.emailSenderTrusted && receipt.emailFromAddress !== null && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <p className="font-medium">This came from an address you don't know.</p>
            <p className="mt-1">
              Forwarded by <strong>{receipt.emailFromAddress}</strong>. It was
              kept so nothing is lost, but check it's really yours before
              filing it.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  trustSender.mutate({
                    id: receipt.id!,
                    patch: { emailSenderTrusted: true },
                  })
                }}
                disabled={trustSender.isPending}
              >
                It's fine — trust this one
              </Button>
            </div>
          </div>
        )}

        {samePurchase !== null && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            <p className="font-medium">Is this the other half of the same bill?</p>
            <p className="mt-1">
              You already have a {samePurchase.transaction.vendor} charge for{' '}
              <Money value={samePurchase.transaction.amount} /> on{' '}
              {formatDate(samePurchase.transaction.transactionDate)}. A restaurant
              gives you two slips — the itemised one and the signed one with the
              tip — and both belong on <b>one</b> charge: both print on the
              packet, and only the tipped total comes off your budget.
            </p>
            {(receipt.extractedAmount ?? 0) > samePurchase.transaction.amount && (
              <p className="mt-1">
                This photo is the higher of the two, so the charge will be
                corrected to <Money value={receipt.extractedAmount ?? 0} /> — the
                amount your statement will show.
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={pending}
                onClick={() => void attachAsSamePurchase(samePurchase.transaction)}
              >
                {pending ? 'Adding…' : 'Add this photo to that charge'}
              </Button>
              <Button
                variant="ghost"
                disabled={pending}
                onClick={() => setDismissedSamePurchase(true)}
              >
                No — this is a separate purchase
              </Button>
            </div>
          </div>
        )}

        {duplicate !== null && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">This may already be filed.</p>
            <p className="mt-1">
              A {duplicate.transaction.vendor} charge for{' '}
              <Money value={duplicate.transaction.amount} /> is already
              recorded on {formatDate(duplicate.transaction.transactionDate)}. If
              this is the same purchase, attach the receipt to it instead of
              creating a second transaction.
            </p>
            <Button
              variant="ghost"
              className="mt-2"
              onClick={() => setMode('attach')}
            >
              Attach to an existing transaction
            </Button>
          </div>
        )}

        {/* Opening a receipt that is already filed used to show the filing
            form, budget code picker empty — a screen asking to be told what
            this receipt is, about a receipt that already knows. The code was
            never missing; it lives on the charge, and this view had never
            looked at one. */}
        {filedAgainst.length > 0 && (
          <Card className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {filedAgainst.length === 1
                ? 'Filed against'
                : `Filed against ${filedAgainst.length} charges`}
            </h2>
            <ul className="space-y-2">
              {filedAgainst.map((charge) => {
                const chargeCode = codesById.get(charge.budgetCodeId)
                return (
                  <li key={charge.id} className="text-sm">
                    <span className="font-medium text-slate-900">{charge.vendor}</span>{' '}
                    · <Money value={charge.amount} className="text-sm" /> ·{' '}
                    {formatDate(charge.transactionDate)}
                    <p className="text-xs text-slate-500">
                      {chargeCode === undefined
                        ? 'Budget code could not be read'
                        : `${chargeCode.shortLabel} · ${chargeCode.code}`}
                    </p>
                    {/* A real button, not the ghost variant: this is the way
                        to correct a charge from here, and as borderless text
                        it read as a label rather than something to press. */}
                    <Button
                      className="mt-1.5"
                      onClick={() => setEditingChargeId(charge.id)}
                    >
                      Edit this charge
                    </Button>
                  </li>
                )
              })}
            </ul>
            <p className="text-xs text-slate-500">
              One bill can sit behind several charges — attach it to another
              below.
            </p>
          </Card>
        )}

        {filedAgainst.length > 0 && !addingAnother ? (
          <button
            type="button"
            onClick={() => setAddingAnother(true)}
            className="self-start text-sm text-slate-600 underline underline-offset-4 hover:text-slate-900"
          >
            This bill covers another charge too
          </button>
        ) : (
        <>
        <div className="flex gap-2 text-sm">
          <ModeTab
            active={mode === 'file'}
            onClick={() => setMode('file')}
            label="Create the charge"
          />
          <ModeTab
            active={mode === 'attach'}
            onClick={() => setMode('attach')}
            label="Add to a charge already here"
          />
        </div>

        {/* The two used to be called "File" and "Attach", which named the
            mechanism rather than the question. The question is only ever
            whether the charge exists yet. */}
        <p className="text-xs text-slate-500">
          {mode === 'attach'
            ? 'The charge is already recorded — from the statement or a CSIU import. This puts the bill behind it and changes nothing else.'
            : 'The charge is not in the app yet. This creates it from the receipt, so it needs a budget code.'}
        </p>

        {mode === 'attach' ? (
          <AttachToExisting
            transactions={transactions}
            receipt={receipt}
            onAttach={(t) => void attachExisting(t)}
            onCancel={onClose}
            pending={pending}
          />
        ) : readiness === 'confirm' && !forceForm ? (
          <ConfirmPanel
            receipt={receipt}
            code={code}
            onCodeChange={setCode}
            description={description}
            onDescriptionChange={setDescription}
            pending={pending}
            onConfirm={() => {
              if (code === null) return
              void fileNew({
                budgetCodeId: code.id ?? '',
                transactionDate: receipt.extractedDate!,
                dateReceived: receipt.extractedDate!,
                amount: receipt.extractedAmount!,
                vendor: receipt.extractedVendor!,
                description: description.trim(),
                source: 'pcard',
                notes: '',
              })
            }}
            onEdit={() => setForceForm(true)}
          />
        ) : (
          <TransactionForm
            submitLabel="File receipt"
            // Both carried across: opening the fuller form used to drop the
            // code that had just been chosen, so it had to be picked twice.
            initialCode={code}
            prefill={{
              vendor: receipt.extractedVendor,
              amount: receipt.extractedAmount,
              transactionDate: receipt.extractedDate,
              description,
              source: 'pcard',
            }}
            onSubmit={(values) => void fileNew(values)}
            onCancel={onClose}
            pending={pending}
          >
            {readiness === 'manual' && (
              <p className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-600">
                Nothing usable was read from this capture, so the form is empty.
                The image is on the left — the original is stored either way.
              </p>
            )}
          </TransactionForm>
        )}
        </>
        )}
      </div>
    </div>
  )
}

function ModeTab({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-lg px-3 py-1.5 font-medium transition ${
        active
          ? 'bg-slate-900 text-white'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      {label}
    </button>
  )
}

/**
 * What was read, and what was refused.
 *
 * A refused field shows its reason rather than just being blank — §6.1's
 * entire premise is that a silent wrong value is worse than a loud empty one.
 */
function ExtractionSummary({
  receipt,
  readiness,
  onRetry,
  retrying,
  retryError,
}: {
  receipt: Receipt
  readiness: 'confirm' | 'correct' | 'manual'
  onRetry: () => void
  retrying: boolean
  retryError?: string | undefined
}) {
  const extraction = receipt.extraction

  if (extraction === null) {
    return (
      <Card className="space-y-2">
        <p className="text-sm font-medium text-slate-900">Not read</p>
        <p className="text-xs text-slate-500">
          This capture predates automatic reading, or reading never ran.
        </p>
        <RetryButton onRetry={onRetry} retrying={retrying} error={retryError} />
      </Card>
    )
  }

  if (extraction.status === 'pending') {
    return (
      <Card>
        <p className="text-sm text-slate-600" role="status">
          Reading the receipt…
        </p>
      </Card>
    )
  }

  if (extraction.status === 'failed') {
    return (
      <Card className="space-y-2">
        <Badge tone="warn">Couldn't read</Badge>
        <p className="text-sm text-slate-700">
          {extraction.failure ?? 'Reading this receipt failed.'}
        </p>
        {/* A permanent failure means the bytes themselves can't be decoded, so
            re-reading them would fail identically. Offering the button anyway
            would just waste a call and the user's time. */}
        {!extraction.permanent && (
          <RetryButton onRetry={onRetry} retrying={retrying} error={retryError} />
        )}
      </Card>
    )
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-2">
        {readiness === 'confirm' ? (
          <Badge tone="ok">Read cleanly</Badge>
        ) : (
          <Badge tone="warn">Needs a check</Badge>
        )}
        {receipt.extractionConfidence !== null && (
          <span className="text-xs text-slate-500">
            {Math.round(receipt.extractionConfidence * 100)}% confident
          </span>
        )}
      </div>

      <dl className="space-y-1.5 text-sm">
        <ReadField label="Vendor" value={receipt.extractedVendor} />
        <ReadField
          label="Amount"
          value={
            receipt.extractedAmount === null ? null : (
              <Money value={receipt.extractedAmount} />
            )
          }
        />
        <ReadField
          label="Date"
          value={
            receipt.extractedDate === null
              ? null
              : formatDate(receipt.extractedDate)
          }
        />
      </dl>

      {extraction.rejections.length > 0 && (
        <ul className="space-y-1.5 border-t border-slate-200 pt-3 text-xs text-slate-600">
          {extraction.rejections.map((rejection, index) => (
            <li key={`${rejection.reason}-${index}`}>
              <span className="font-medium text-slate-800">
                {describeRejection(rejection)}
              </span>
              {rejection.reason !== 'low_confidence' && (
                <span className="block text-slate-500">
                  Read: “{rejection.rejected}”
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {extraction.dateRejected && (
        <p className="border-t border-slate-200 pt-3 text-xs text-slate-600">
          A date was found but fell outside a plausible range, so it was
          discarded. Check the year.
        </p>
      )}

      {extraction.notes !== null && extraction.notes !== '' && (
        <p className="border-t border-slate-200 pt-3 text-xs text-slate-500">
          {extraction.notes}
        </p>
      )}
    </Card>
  )
}

function RetryButton({
  onRetry,
  retrying,
  error,
}: {
  onRetry: () => void
  retrying: boolean
  error?: string | undefined
}) {
  return (
    <div className="space-y-1.5">
      <Button onClick={onRetry} disabled={retrying}>
        {retrying ? 'Reading…' : 'Try reading it again'}
      </Button>
      {error !== undefined && (
        <p role="alert" className="text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  )
}

function ReadField({
  label,
  value,
}: {
  label: string
  value: React.ReactNode | null
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className={value === null ? 'text-amber-700' : 'text-slate-900'}>
        {value ?? 'refused'}
      </dd>
    </div>
  )
}

/** The fast path: everything read cleanly, so only the code is missing. */
function ConfirmPanel({
  receipt,
  code,
  onCodeChange,
  description,
  onDescriptionChange,
  onConfirm,
  onEdit,
  pending,
}: {
  receipt: Receipt
  code: BudgetCode | null
  onCodeChange: (code: BudgetCode | null) => void
  description: string
  onDescriptionChange: (description: string) => void
  onConfirm: () => void
  onEdit: () => void
  pending: boolean
}) {
  return (
    <div className="space-y-5">
      <Card className="space-y-1">
        <p className="text-lg font-medium text-slate-900">
          {receipt.extractedVendor}
        </p>
        <Money value={receipt.extractedAmount ?? 0} className="text-xl" />
        <p className="text-sm text-slate-500">
          {formatDate(receipt.extractedDate)}
        </p>
      </Card>

      <BudgetCodeField value={code} onChange={onCodeChange} />

      {/* Here rather than behind "Change something": the description is the
          one field this panel could not supply, and every receipt needs one
          for the log sheet. Requiring the full form for it made the quick
          path the slow path. */}
      <Field label="Description" hint="Brief — this prints on the packet.">
        <Input
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
          placeholder="Hotel for Cyber Conference"
          maxLength={200}
        />
      </Field>

      {code?.external === true && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This is {code.externalOwnerName}'s code. The charge appears on your
          packet but counts against none of your balances.
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={pending || code === null}
        >
          {pending ? 'Filing…' : 'Looks right — file it'}
        </Button>
        <Button onClick={onEdit} disabled={pending}>
          Change something
        </Button>
      </div>

      {code === null && (
        <p className="text-sm text-slate-500">
          Choose a budget code to file this receipt.
        </p>
      )}
    </div>
  )
}

/** Attach to a transaction that already exists — the duplicate's remedy. */
function AttachToExisting({
  transactions,
  receipt,
  onAttach,
  onCancel,
  pending,
}: {
  transactions: readonly Transaction[]
  receipt: Receipt
  onAttach: (transaction: Transaction) => void
  onCancel: () => void
  pending: boolean
}) {
  const [search, setSearch] = useState('')

  // Transactions with no receipt yet come first — those are the ones a loose
  // receipt most likely belongs to.
  const ranked = useMemo(() => {
    const term = search.trim().toLowerCase()
    const matches = transactions.filter(
      (t) =>
        term === '' ||
        t.vendor.toLowerCase().includes(term) ||
        t.description.toLowerCase().includes(term),
    )
    return [...matches]
      .sort((a, b) => {
        const aHas = a.receiptIds.length > 0 ? 1 : 0
        const bHas = b.receiptIds.length > 0 ? 1 : 0
        if (aHas !== bHas) return aHas - bHas
        return b.transactionDate.localeCompare(a.transactionDate)
      })
      .slice(0, 25)
  }, [transactions, search])

  return (
    <div className="space-y-4">
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by vendor or description"
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        autoComplete="off"
      />

      {ranked.length === 0 ? (
        <p className="text-sm text-slate-500">
          No transactions match. File this as a new transaction instead.
        </p>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
          {ranked.map((transaction) => (
            <li key={transaction.id}>
              <button
                type="button"
                onClick={() => onAttach(transaction)}
                disabled={pending}
                className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-slate-50 disabled:opacity-50"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-900">
                    {transaction.vendor}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {formatDate(transaction.transactionDate)}
                    {transaction.receiptIds.length > 0 &&
                      ` · already has ${transaction.receiptIds.length} receipt${
                        transaction.receiptIds.length === 1 ? '' : 's'
                      }`}
                  </span>
                </span>
                <Money value={transaction.amount} className="text-sm" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {receipt.extractedAmount !== null && (
        <p className="text-xs text-slate-500">
          This receipt reads{' '}
          <Money value={receipt.extractedAmount} /> — check the amounts
          match before attaching.
        </p>
      )}

      <Button onClick={onCancel} disabled={pending}>
        Cancel
      </Button>
    </div>
  )
}

/**
 * The receipt itself, shown while filing it.
 *
 * PDFs go in an `<iframe>` rather than a placeholder. Every browser has a
 * built-in PDF viewer, and "Preview after processing" was a promise nothing
 * kept — the page images it referred to are a Phase 5 concern, so until then
 * the most common emailed receipt format was simply unviewable at the moment
 * you needed to read it to file it.
 *
 * HEIC still can't be shown: no browser outside Apple's renders it, and the
 * bytes here are the untouched original. Once extraction converts one, that
 * conversion is what gets displayed.
 */
function ReceiptImage({ receipt }: { receipt: Receipt }) {
  const isPdf = receipt.originalFileUrl.endsWith('.pdf')
  const isHeic = /\.hei[cf]$/.test(receipt.originalFileUrl)
  const [failed, setFailed] = useState(false)

  const { data: url } = useQuery({
    queryKey: ['receiptUrl', receipt.originalFileUrl],
    queryFn: () => receiptFileUrl(receipt.originalFileUrl),
    staleTime: 30 * 60 * 1000,
  })

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
        {url === undefined ? (
          <div className="flex h-48 items-center justify-center text-sm text-slate-500">
            Loading…
          </div>
        ) : isPdf ? (
          <iframe
            src={url}
            title="Receipt PDF"
            className="h-[28rem] w-full border-0 bg-white"
          />
        ) : !isHeic && !failed ? (
          <img
            src={url}
            alt="Receipt"
            className="max-h-[28rem] w-full object-contain"
            onError={() => setFailed(true)}
          />
        ) : (
          <div className="flex h-48 items-center justify-center px-4 text-center text-sm text-slate-500">
            {isHeic
              ? "iPhone photo — browsers can't display this format. Open it to view."
              : 'Preview unavailable'}
          </div>
        )}
      </div>

      {url !== undefined && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-slate-600 underline underline-offset-2 hover:text-slate-900"
        >
          Open the original
        </a>
      )}
    </div>
  )
}
